import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../styles/LanguageTypography.css';
import {
    faBox,
    faBarcode,
    faTag,
    faBuilding,
    faRupeeSign,
    faCalculator,
    faExclamationTriangle,
    faCalendarAlt,
    faSave,
    faTimes,
    faCheckCircle,
    faStore,
    faExchangeAlt,
    faWarehouse,
    faPercent,
    faQrcode,
    faBoxes,
    faLayerGroup,
    faCubes,
    faFileAlt,
    faSpinner,
    faChevronDown,
    faPalette,
    faRuler,
    faNetworkWired,
    faServer,
    faPlus,
    faInfoCircle,
    faSearch,
    faUserTie,
    faShieldAlt,
    faBalanceScale
} from '@fortawesome/free-solid-svg-icons';

// This must be the same server file as the PHP file supplied with this form.
const PRODUCTS_API_URL = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php';
const RTL_LANGUAGE_CODES = new Set(['ur', 'ps', 'ar', 'fa', 'prs']);
const sourceValue = (record, field, fallback = '') =>
    record?._original?.[field] ?? record?.[field] ?? fallback;

// ==============================================
// GLOBAL BARCODE SCANNER HOOK
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null);

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 40;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 150;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { /* ignore */ }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const handleKeyDown = (e) => {
            if (!bufferRef.current) {
                bufferRef.current = '';
            }

            if (!e || typeof e.key !== 'string') {
                resetBuffer();
                return;
            }

            if (e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (e.key === 'Shift' || e.key === 'CapsLock' || e.key === 'Tab') {
                return;
            }

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (e.key === 'Enter') {
                clearTimeout(scanTimerRef.current);
                const candidate = (bufferRef.current || '').trim();
                const isConfirmedScan = isLikelyScanningRef.current && candidate.length >= MIN_BARCODE_LENGTH;

                if (isConfirmedScan) {
                    e.preventDefault();
                    e.stopPropagation();
                    restoreLeak();

                    if (!isProcessingRef.current) {
                        isProcessingRef.current = true;
                        onBarcodeScanned(candidate);
                        setTimeout(() => { isProcessingRef.current = false; }, 500);
                    }
                }
                resetBuffer();
                return;
            }

            if (e.key.length !== 1) {
                resetBuffer();
                return;
            }

            const currentBuffer = bufferRef.current || '';
            
            if (currentBuffer.length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                bufferRef.current = e.key;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable ? {
                    element: target,
                    value: target.value,
                    selectionStart: target.selectionStart
                } : null;
            } else {
                bufferRef.current = currentBuffer + e.key;
                isLikelyScanningRef.current = true;
                if (isEditable) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(resetBuffer, BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

// ==============================================
// TRANSLATOR
// ==============================================
const createProductsTranslator = () => {
    let cache = new Map();

    const urduTranslations = {
        addProduct: 'محفوظ کریں',
        editProduct: 'ترمیم کریں',
        fillDetails: 'براہ کرم تفصیلات درج کریں',
        productDetails: 'مصنوعات کی تفصیلات',
        productName: 'مصنوعات کا نام',
        productCode: 'مصنوعات کا کوڈ',
        batchNumber: 'بیچ نمبر',
        barcode: 'بارکوڈ',
        category: 'زمرہ',
        company: 'کمپنی / برانڈ',
        color: 'رنگ',
        size: 'سائز',
        gauge: 'گیج',
        weight: 'وزن',
        class: 'کلاس',
        unit: 'یونٹ',
        costPrice: 'لاگت قیمت',
        sellingPrice: 'فروخت قیمت',
        quantity: 'مقدار',
        quantityAlert: 'کم اسٹاک الرٹ',
        expiryDate: 'میعاد ختم ہونے کی تاریخ',
        date: 'تاریخ',
        addProductButton: 'محفوظ کریں',
        updateProduct: 'اپ ڈیٹ کریں',
        clearButton: 'صاف کریں',
        adding: 'محفوظ ہو رہا ہے...',
        updating: 'اپ ڈیٹ ہو رہا ہے...',
        productSaved: 'مصنوعات کامیابی سے محفوظ ہو گئی!',
        productUpdated: 'مصنوعات کامیابی سے اپ ڈیٹ ہو گئی!',
        failedAdd: 'مصنوعات محفوظ کرنے میں ناکامی',
        failedUpdate: 'مصنوعات اپ ڈیٹ کرنے میں ناکامی',
        networkError: 'نیٹ ورک کی خرابی',
        nameRequired: 'مصنوعات کا نام ضروری ہے',
        nameTooShort: 'نام کم از کم 2 حروف کا ہونا چاہیے',
        batchRequired: 'بیچ نمبر ضروری ہے',
        batchExists: 'یہ بیچ نمبر پہلے سے موجود ہے',
        productCodeExists: 'یہ مصنوعات کا کوڈ پہلے سے موجود ہے',
        unitRequired: 'یونٹ ضروری ہے',
        costPriceRequired: 'لاگت قیمت ضروری ہے',
        sellingPriceRequired: 'فروخت قیمت ضروری ہے',
        quantityRequired: 'مقدار ضروری ہے',
        alertRequired: 'کم اسٹاک الرٹ ضروری ہے',
        invalidPrice: 'براہ کرم درست قیمت درج کریں',
        invalidQuantity: 'براہ کرم درست مقدار درج کریں',
        expiryPast: 'میعاد ختم ہونے کی تاریخ ماضی میں نہیں ہو سکتی',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        selectShop: 'پہلے دکان منتخب کریں',
        currentShop: 'موجودہ دکان',
        willAddToShop: 'مصنوعات اس دکان میں شامل ہو گی',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان تبدیل کریں',
        switchShop: 'دکان تبدیل کریں',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل کرنے میں ناکامی',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        active: 'فعال',
        current: 'موجودہ',
        profitMargin: 'منافع کا مارجن',
        markup: 'مارک اپ',
        selectCategory: 'زمرہ منتخب کریں',
        selectUnit: 'یونٹ منتخب کریں',
        optional: 'اختیاری',
        enterProductName: 'مصنوعات کا نام درج کریں',
        enterProductCode: 'مصنوعات کا کوڈ درج کریں',
        enterBatchNumber: 'بیچ نمبر درج کریں (اختیاری)',
        barcodeAutoGenerate: 'بارکوڈ خودکار جنریٹ ہو گا',
        enterCompany: 'کمپنی/برانڈ درج کریں',
        zeroPlaceholder: '0.0',
        zeroPlaceholderDecimal: '0.00',
        zeroPlaceholderThreeDecimal: '0.000',
        packingType: 'پیکنگ کی قسم',
        subPackingType: 'ذیلی پیکنگ کی قسم',
        itemsPerPack: 'فی پیک اشیاء',
        unitOfMeasure: 'پیمائش کی اکائی',
        totalQuantity: 'کل مقدار',
        costPerPack: 'فی پیک لاگت',
        sellingPerPack: 'فی پیک فروخت قیمت',
        selectPackingType: 'پیکنگ کی قسم منتخب کریں',
        enterItemsPerPack: 'فی پیک اشیاء کی تعداد درج کریں',
        costPerUnit: 'فی یونٹ لاگت',
        sellPricePerUnit: 'فی یونٹ فروخت قیمت',
        bquantity: 'کل مقدار (بلک)',
        box: 'باکس',
        carton: 'کارٹن',
        crate: 'کریٹ',
        packet: 'پیکٹ',
        bundle: 'بنڈل',
        bottle: 'بوتل',
        can: 'کین',
        piece: 'ٹکڑا',
        '1_liter': '1 لیٹر',
        '2_liter': '2 لیٹر',
        '3_liter': '3 لیٹر',
        '5_liter': '5 لیٹر',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف کر دیا گیا',
        draftExists: 'پہلے سے محفوظ ڈرافٹ موجود ہے',
        batchAutoGenerated: 'بیچ نمبر خودکار جنریٹ ہو گیا: ',
        productFound: 'پروڈکٹ مل گیا',
        productNotFound: 'پروڈکٹ نہیں ملا',
        barcodeSet: 'بارکوڈ سیٹ کر دیا گیا',
        scannerActive: 'اسکینر فعال ہے',
        centralizedShops: 'مرکزی دکانیں',
        assignCentralizedShops: 'مرکزی دکانیں تفویض کریں',
        selectCentralizedShops: 'مرکزی دکانیں منتخب کریں',
        noCentralizedShops: 'کوئی مرکزی دکانیں تفویض نہیں',
        centralizedAccess: 'مرکزی رسائی',
        centralizedProduct: 'مرکزی مصنوعات',
        addShop: 'دکان شامل کریں',
        productWillBeAvailable: 'مصنوعات اس دکان میں دستیاب ہو گی',
        supplier: 'سپلائر',
        selectSupplier: 'سپلائر منتخب کریں'
    };

    const defaultTexts = {
        addProduct: 'Add Product',
        editProduct: 'Edit Stock',
        fillDetails: 'Fill in the stock details below',
        productDetails: 'Product Details',
        productName: 'Product Name',
        warranty: 'Warranty',
        productCode: 'Product Code / Grade',
        batchNumber: 'Batch Number',
        barcode: 'Barcode',
        category: 'Category',
        company: 'Company / Brand',
        color: 'Color',
        size: 'Size',
        gauge: 'Gauge',
        weight: 'Weight',
        class: 'Class',
        unit: 'Unit',
        costPrice: 'Cost Price',
        sellingPrice: 'Selling Price',
        quantity: 'Quantity',
        quantityAlert: 'Low Stock Alert',
        expiryDate: 'Expiry Date',
        date: 'Date',
        addProductButton: 'Save Product',
        updateProduct: 'Update Product',
        clearButton: 'Clear',
        adding: 'Adding...',
        updating: 'Updating...',
        productSaved: 'Product saved successfully!',
        productUpdated: 'Product updated successfully!',
        failedAdd: 'Failed to save product',
        failedUpdate: 'Failed to update product',
        networkError: 'Network error occurred',
        nameRequired: 'Product name is required',
        nameTooShort: 'Name must be at least 2 characters',
        batchRequired: 'Batch number is required',
        batchExists: 'This batch number already exists',
        productCodeExists: 'This product code already exists',
        unitRequired: 'Unit is required',
        costPriceRequired: 'Cost price is required',
        sellingPriceRequired: 'Selling price is required',
        quantityRequired: 'Quantity is required',
        alertRequired: 'Low stock alert quantity is required',
        invalidPrice: 'Please enter a valid price',
        invalidQuantity: 'Please enter a valid quantity',
        expiryPast: 'Expiry date cannot be in the past',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Product will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current',
        profitMargin: 'Profit Margin',
        markup: 'Markup',
        selectCategory: 'Select category',
        selectUnit: 'Select unit',
        optional: 'Optional',
        enterProductName: 'Enter product name',
        enterProductCode: 'Enter product code',
        enterBatchNumber: 'Enter batch number (optional)',
        barcodeAutoGenerate: 'Barcode will auto-generate',
        enterCompany: 'Enter company/brand',
        zeroPlaceholder: '0.0',
        zeroPlaceholderDecimal: '0.00',
        zeroPlaceholderThreeDecimal: '0.000',
        packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type',
        itemsPerPack: 'Items Per',
        unitOfMeasure: 'Unit of Measure',
        totalQuantity: 'Total Quantity',
        costPerPack: 'Cost',
        sellingPerPack: 'Selling Price',
        selectPackingType: 'Select packing type',
        enterItemsPerPack: 'Enter items per pack',
        costPerUnit: 'Cost per',
        sellPricePerUnit: 'Sell price per',
        bquantity: 'BQuantity',
        box: 'Box',
        carton: 'Carton',
        crate: 'Crate',
        packet: 'Packet',
        bundle: 'Bundle',
        bottle: 'Bottle',
        can: 'Can',
        piece: 'Piece',
        '1_liter': '1 Liter',
        '2_liter': '2 Liters',
        '3_liter': '3 Liters',
        '5_liter': '5 Liters',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        draftExists: 'A saved draft exists',
        batchAutoGenerated: 'Batch number auto-generated: ',
        productFound: 'Product found!',
        productNotFound: 'Product not found',
        barcodeSet: 'Barcode set',
        scannerActive: 'Scanner Active',
        centralizedShops: 'Centralized Shops',
        assignCentralizedShops: 'Assign Centralized Shops',
        selectCentralizedShops: 'Select Centralized Shops',
        noCentralizedShops: 'No centralized shops assigned',
        centralizedAccess: 'Centralized Access',
        centralizedProduct: 'Centralized Product',
        addShop: 'Add Shop',
        productWillBeAvailable: 'Product will be available in',
        supplier: 'Supplier',
        selectSupplier: 'Select Supplier'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        if (lang === 'ur') return urduTranslations;
        return defaultTexts;
    };

    return { getTranslations };
};

const productsTranslator = createProductsTranslator();

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const generateBatchNoFromProductName = (productName, existingBatchNos = []) => {
    if (!productName || productName.trim() === '') {
        return '';
    }
    
    let cleanName = productName.trim().toUpperCase();
    cleanName = cleanName.replace(/[^A-Z0-9]/g, '');
    
    if (cleanName.length === 0) {
        cleanName = 'XXX';
    } else if (cleanName.length === 1) {
        cleanName = cleanName + 'XX';
    } else if (cleanName.length === 2) {
        cleanName = cleanName + 'X';
    }
    
    const namePart = cleanName.substring(0, 3);
    let highestNumber = 0;
    const prefix = namePart;
    
    existingBatchNos.forEach(batch => {
        if (batch && batch.startsWith(prefix)) {
            const numericPart = batch.substring(3);
            if (numericPart && /^\d{4}$/.test(numericPart)) {
                const num = parseInt(numericPart, 10);
                if (num > highestNumber) {
                    highestNumber = num;
                }
            }
        }
    });
    
    const nextNumber = highestNumber + 1;
    const numericStr = String(nextNumber).padStart(4, '0');
    return `${namePart}${numericStr}`;
};

const generateBarcodeFromProductName = (productName, username, shopId) => {
    if (!productName || productName.trim() === '') return '';
    if (!username || !shopId) return '';

    const cleanName = productName.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    let namePart = cleanName.substring(0, 3);
    if (namePart.length < 3) {
        namePart = namePart.padEnd(3, 'X');
    }
    
    let shopPrefix = shopId.toString().substring(0, 2).replace(/[^0-9]/g, '0');
    if (shopPrefix.length < 2) {
        shopPrefix = shopPrefix.padEnd(2, '0');
    }
    
    let userPrefix = username.substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, 'P');
    if (userPrefix.length < 3) {
        userPrefix = userPrefix.padEnd(3, 'P');
    }
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    for (let i = 0; i < 4; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return `${namePart}${randomPart}${shopPrefix}${userPrefix}`;
};

const requestTracker = new Map();

function isDuplicateRequest(requestData) {
    const key = JSON.stringify({
        action: requestData.action,
        product_name: requestData.product_name,
        batch_no: requestData.batch_no,
        barcode: requestData.barcode,
        product_code: requestData.product_code,
        shop_id: requestData.shop_id
    });

    const now = Date.now();
    const lastRequest = requestTracker.get(key);

    if (lastRequest && (now - lastRequest) < 5000) {
        return true;
    }

    requestTracker.set(key, now);

    for (const [k, time] of requestTracker.entries()) {
        if (now - time > 10000) {
            requestTracker.delete(k);
        }
    }

    return false;
}

// ==============================================
// TOAST NOTIFICATION COMPONENT
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            case 'info':
                return faCheckCircle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            case 'info':
                return 'Info';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// SHOP SELECTOR MODAL COMPONENT
// ==============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                <div className="ozui_modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                    ) : (
                        shops.map((shop) => (
                            <div 
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const SolarSpecialProducts = ({ productId = null, onSuccess = null }) => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return user?.language || localStorage.getItem('ourzest_language') || 'en';
    });

    // Auto-focus ref for Product Name field
    const productNameRef = useRef(null);

    // ==============================================
    // OPTIONS
    // ==============================================
    const gaugeOptions = [
        { value: '', label: 'Select Gauge' },
        { value: '10', label: '10' },
        { value: '12', label: '12' },
        { value: '14', label: '14' },
        { value: '16', label: '16' },
        { value: '18', label: '18' },
        { value: '20', label: '20' },
        { value: '22', label: '22' },
        { value: '24', label: '24' },
        { value: '26', label: '26' },
        { value: '28', label: '28' },
        { value: '30', label: '30' },
        { value: 'custom', label: 'Custom' }
    ];

    const weightOptions = [
        { value: '', label: 'Select Weight' },
        { value: '0.5', label: '0.5 kg' },
        { value: '1', label: '1 kg' },
        { value: '1.5', label: '1.5 kg' },
        { value: '2', label: '2 kg' },
        { value: '2.5', label: '2.5 kg' },
        { value: '3', label: '3 kg' },
        { value: '5', label: '5 kg' },
        { value: '10', label: '10 kg' },
        { value: '20', label: '20 kg' },
        { value: 'custom', label: 'Custom' }
    ];

    const classOptions = [
        { value: '', label: 'Select Class' },
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
        { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
        { value: 'H', label: 'H' },
        { value: 'I', label: 'I' },
        { value: 'J', label: 'J' },
        { value: 'K', label: 'K' },
        { value: 'L', label: 'L' },
        { value: 'M', label: 'M' },
        { value: 'N', label: 'N' },
        { value: 'O', label: 'O' },
        { value: 'P', label: 'P' },
        { value: 'Q', label: 'Q' },
        { value: 'R', label: 'R' },
        { value: 'S', label: 'S' },
        { value: 'T', label: 'T' },
        { value: 'U', label: 'U' },
        { value: 'V', label: 'V' },
        { value: 'W', label: 'W' },
        { value: 'X', label: 'X' },
        { value: 'Y', label: 'Y' },
        { value: 'Z', label: 'Z' }
    ];

    // ==============================================
    // STATE DECLARATIONS
    // ==============================================
    
    // Form state
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        batch_no: '',
        barcode: '',
        product_name: '',
        warranty: '',
        product_code: '',
        category: '',
        company: '',
        color: '',
        size: '',
        gauge: '',
        weight: '',
        class: '',
        gunit: '',
        supplier_id: '',
        supplier_name: '',
        packing_type: 'packet',
        sub_packing_type: 'piece',
        items_per_pack: '1',
        unit_of_measure: 'piece',
        cost_price_per_pack: '',
        sell_price_per_unit: '',
        total_quantity: '',
        quantity_alert: '2',
        expiry_date: '',
        cost_per_unit: '',
        sell_per_pack: '',
        bquantity: '',
        purchase_type: 'packing',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1',
        batch_no_manual: false,
        barcode_manual: false,
        existing_batch_nos: [],
        is_product_centralized: 0,
        centralized_shops: []
    });

    // Unit of Measure options
    const unitOptions = [
        { value: '', label: 'Select Unit' },
        { value: 'Units', label: 'Units' },
        { value: 'Yard', label: 'Yard' },
        { value: 'Feet', label: 'Feet' },
        { value: 'Set', label: 'Set' },
        { value: 'Dozen', label: 'Dozen' },
        { value: 'Box', label: 'Box' }
    ];

    // Supplier state
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);

    // Centralized Shops State
    const [isProductCentralized, setIsProductCentralized] = useState(false);
    const [centralizedShopIds, setCentralizedShopIds] = useState([]);
    const [allAvailableShopsForCentralized, setAllAvailableShopsForCentralized] = useState([]);
    const [showCentralizedShopDropdown, setShowCentralizedShopDropdown] = useState(false);
    const [centralizedShopSearchTerm, setCentralizedShopSearchTerm] = useState('');
    const [centralizedDropdownRef, setCentralizedDropdownRef] = useState(null);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [translations, setTranslations] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    
    // Manual dropdown states
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [showColorDropdown, setShowColorDropdown] = useState(false);
    const [colorSearchTerm, setColorSearchTerm] = useState('');
    const [showSizeDropdown, setShowSizeDropdown] = useState(false);
    const [sizeSearchTerm, setSizeSearchTerm] = useState('');
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);
    const [unitSearchTerm, setUnitSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [showGaugeDropdown, setShowGaugeDropdown] = useState(false);
    const [gaugeSearchTerm, setGaugeSearchTerm] = useState('');
    const [showWeightDropdown, setShowWeightDropdown] = useState(false);
    const [weightSearchTerm, setWeightSearchTerm] = useState('');
    const [showClassDropdown, setShowClassDropdown] = useState(false);
    const [classSearchTerm, setClassSearchTerm] = useState('');
    
    // Refs for dropdowns
    const categoryDropdownRef = useRef(null);
    const colorDropdownRef = useRef(null);
    const sizeDropdownRef = useRef(null);
    const unitDropdownRef = useRef(null);
    const supplierDropdownRef = useRef(null);
    const gaugeDropdownRef = useRef(null);
    const weightDropdownRef = useRef(null);
    const classDropdownRef = useRef(null);
    
    // State for calculated display values
    const [calculatedCostPerUnit, setCalculatedCostPerUnit] = useState('');
    const [calculatedSellPerPack, setCalculatedSellPerPack] = useState('');
    const [calculatedBQuantity, setCalculatedBQuantity] = useState('');

    // ==============================================
    // BARCODE SCANNER STATE
    // ==============================================
    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    const isScanningRef = useRef(false);

    // Refs for preventing duplicate submissions
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const lastSubmitTimeRef = useRef(0);
    const lastProductDataRef = useRef('');
    const isInitialLoadRef = useRef(true);
    const isClearingRef = useRef(false);

    // ==============================================
    // CATEGORY, COLOR, SIZE OPTIONS
    // ==============================================
    const categoryOptions = [
        { value: '', label: 'Select Category' },
        { value: 'solar_panels', label: 'SOLAR PANELS' },
        { value: 'hybrid_inverters', label: 'HYBRID INVERTERS' },
        { value: 'direct_inverters', label: 'DIRECT INVERTERS' },
        { value: 'vfd_inverters', label: 'VFD INVERTERS' },
        { value: 'battery', label: 'BATTERY' },
        { value: 'submersibles', label: 'SUBMERSIBLES' },
        { value: 'solar_frame', label: 'SOLAR FRAME' },
        { value: 'cables', label: 'CABLES' },
        { value: 'pipes', label: 'PIPES' },
        { value: 'db_boxes', label: 'DB BOXES' },
        { value: 'hardware_items', label: 'HARDWARE ITEMS' },
        { value: 'electronics_items', label: 'ELECTRONICS ITEMS' },
        { value: 'solar_brushes', label: 'SOLAR BRUSHES' }
    ];

    const colorOptions = [
        { value: '', label: 'Select Color' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'green', label: 'Green' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'pink', label: 'Pink' },
        { value: 'brown', label: 'Brown' },
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'gray', label: 'Gray' },
        { value: 'silver', label: 'Silver' },
        { value: 'gold', label: 'Gold' },
        { value: 'beige', label: 'Beige' },
        { value: 'cream', label: 'Cream' },
        { value: 'maroon', label: 'Maroon' },
        { value: 'navy', label: 'Navy Blue' },
        { value: 'teal', label: 'Teal' },
        { value: 'turquoise', label: 'Turquoise' },
        { value: 'lime', label: 'Lime' },
        { value: 'olive', label: 'Olive' },
        { value: 'coral', label: 'Coral' },
        { value: 'salmon', label: 'Salmon' },
        { value: 'lavender', label: 'Lavender' },
        { value: 'violet', label: 'Violet' },
        { value: 'magenta', label: 'Magenta' },
        { value: 'cyan', label: 'Cyan' },
        { value: 'indigo', label: 'Indigo' },
        { value: 'amber', label: 'Amber' },
        { value: 'peach', label: 'Peach' },
        { value: 'mint', label: 'Mint' },
        { value: 'sage', label: 'Sage' },
        { value: 'tan', label: 'Tan' },
        { value: 'ivory', label: 'Ivory' },
        { value: 'charcoal', label: 'Charcoal' },
        { value: 'slate', label: 'Slate' },
        { value: 'champagne', label: 'Champagne' },
        { value: 'rose', label: 'Rose' },
        { value: 'crimson', label: 'Crimson' },
        { value: 'burgundy', label: 'Burgundy' },
        { value: 'plum', label: 'Plum' },
        { value: 'mauve', label: 'Mauve' },
        { value: 'fawn', label: 'Fawn' },
        { value: 'mustard', label: 'Mustard' },
        { value: 'rust', label: 'Rust' },
        { value: 'terracotta', label: 'Terracotta' },
        { value: 'copper', label: 'Copper' },
        { value: 'bronze', label: 'Bronze' },
        { value: 'metallic', label: 'Metallic' },
        { value: 'multi', label: 'Multi-Color' },
        { value: 'transparent', label: 'Transparent' },
        { value: 'other', label: 'Other' }
    ];

    const sizeOptions = [
        { value: '', label: 'Select Size' },
        { value: 's', label: 'S' },
        { value: 'm', label: 'M' },
        { value: 'l', label: 'L' },
        { value: 'xl', label: 'XL' },
        { value: 'xxl', label: 'XXL' },
        { value: 'xxxl', label: 'XXXL' },
        { value: '4xl', label: '4XL' },
        { value: '5xl', label: '5XL' },
        { value: '6xl', label: '6XL' },
        { value: '7xl', label: '7XL' },
        { value: '8xl', label: '8XL' },
        { value: '0', label: '0' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
        { value: '7', label: '7' },
        { value: '8', label: '8' },
        { value: '9', label: '9' },
        { value: '10', label: '10' },
        { value: '11', label: '11' },
        { value: '12', label: '12' },
        { value: '13', label: '13' },
        { value: '14', label: '14' },
        { value: '1_cm', label: '1 cm' },
        { value: '2_cm', label: '2 cm' },
        { value: '3_cm', label: '3 cm' },
        { value: '4_cm', label: '4 cm' },
        { value: '5_cm', label: '5 cm' },
        { value: '6_cm', label: '6 cm' },
        { value: '7_cm', label: '7 cm' },
        { value: '8_cm', label: '8 cm' },
        { value: '9_cm', label: '9 cm' },
        { value: '10_cm', label: '10 cm' },
        { value: '11_cm', label: '11 cm' },
        { value: '12_cm', label: '12 cm' },
        { value: '13_cm', label: '13 cm' },
        { value: '14_cm', label: '14 cm' },
        { value: '15_cm', label: '15 cm' },
        { value: '16_cm', label: '16 cm' },
        { value: '17_cm', label: '17 cm' },
        { value: '18_cm', label: '18 cm' },
        { value: '19_cm', label: '19 cm' },
        { value: '20_cm', label: '20 cm' },
        { value: '22_cm', label: '22 cm' },
        { value: '24_cm', label: '24 cm' },
        { value: '25_cm', label: '25 cm' },
        { value: '26_cm', label: '26 cm' },
        { value: '28_cm', label: '28 cm' },
        { value: '30_cm', label: '30 cm' },
        { value: '32_cm', label: '32 cm' },
        { value: '35_cm', label: '35 cm' },
        { value: '40_cm', label: '40 cm' },
        { value: '45_cm', label: '45 cm' },
        { value: '50_cm', label: '50 cm' },
        { value: '55_cm', label: '55 cm' },
        { value: '60_cm', label: '60 cm' },
        { value: '65_cm', label: '65 cm' },
        { value: '70_cm', label: '70 cm' },
        { value: '75_cm', label: '75 cm' },
        { value: '80_cm', label: '80 cm' },
        { value: '85_cm', label: '85 cm' },
        { value: '90_cm', label: '90 cm' },
        { value: '95_cm', label: '95 cm' },
        { value: '100_cm', label: '100 cm' },
        { value: '1_ft', label: '1 ft' },
        { value: '2_ft', label: '2 ft' },
        { value: '3_ft', label: '3 ft' },
        { value: '4_ft', label: '4 ft' },
        { value: '5_ft', label: '5 ft' },
        { value: '6_ft', label: '6 ft' },
        { value: '7_ft', label: '7 ft' },
        { value: '8_ft', label: '8 ft' },
        { value: '9_ft', label: '9 ft' },
        { value: '10_ft', label: '10 ft' },
        { value: '11_ft', label: '11 ft' },
        { value: '12_ft', label: '12 ft' },
        { value: '13_ft', label: '13 ft' },
        { value: '14_ft', label: '14 ft' },
        { value: '15_ft', label: '15 ft' },
        { value: '16_ft', label: '16 ft' },
        { value: '17_ft', label: '17 ft' },
        { value: '18_ft', label: '18 ft' },
        { value: '19_ft', label: '19 ft' },
        { value: '20_ft', label: '20 ft' },
        { value: '0.5_m', label: '0.5 m' },
        { value: '1_m', label: '1 m' },
        { value: '1.5_m', label: '1.5 m' },
        { value: '2_m', label: '2 m' },
        { value: '2.5_m', label: '2.5 m' },
        { value: '3_m', label: '3 m' },
        { value: '4_m', label: '4 m' },
        { value: '5_m', label: '5 m' },
        { value: '6_m', label: '6 m' },
        { value: '7_m', label: '7 m' },
        { value: '8_m', label: '8 m' },
        { value: '9_m', label: '9 m' },
        { value: '10_m', label: '10 m' },
        { value: '12_m', label: '12 m' },
        { value: '15_m', label: '15 m' },
        { value: '20_m', label: '20 m' },
        { value: '25_m', label: '25 m' },
        { value: '30_m', label: '30 m' },
        { value: '40_m', label: '40 m' },
        { value: '50_m', label: '50 m' },
        { value: '60_m', label: '60 m' },
        { value: '70_m', label: '70 m' },
        { value: '80_m', label: '80 m' },
        { value: '90_m', label: '90 m' },
        { value: '100_m', label: '100 m' },
        { value: '60_gsm', label: '60 GSM' },
        { value: '70_gsm', label: '70 GSM' },
        { value: '80_gsm', label: '80 GSM' },
        { value: '90_gsm', label: '90 GSM' },
        { value: '100_gsm', label: '100 GSM' },
        { value: '120_gsm', label: '120 GSM' },
        { value: '140_gsm', label: '140 GSM' },
        { value: '160_gsm', label: '160 GSM' },
        { value: '180_gsm', label: '180 GSM' },
        { value: '200_gsm', label: '200 GSM' },
        { value: '250_gsm', label: '250 GSM' },
        { value: '300_gsm', label: '300 GSM' },
        { value: '350_gsm', label: '350 GSM' },
        { value: 'one_size', label: 'One Size' },
        { value: 'free_size', label: 'Free Size' },
        { value: 'adjustable', label: 'Adjustable' },
        { value: 'custom', label: 'Custom' },
        { value: 'other', label: 'Other' }
    ];

    // ==============================================
    // HELPER FUNCTIONS
    // ==============================================
    const getUnitNumericValue = (unitOfMeasure) => {
        const unitValues = {
            'piece': 1,
            'dozen': 12,
            'box': 1,
            'packet': 1,
            'can': 1,
            'bottle': 1
        };
        return unitValues[unitOfMeasure] || 1;
    };

    const getUnitDisplayLabel = (unitOfMeasure) => {
        const labels = {
            'piece': 'Piece',
            'dozen': 'Dozen',
            'box': 'Box',
            'packet': 'Packet',
            'can': 'Can',
            'bottle': 'Bottle'
        };
        return labels[unitOfMeasure] || 'Unit';
    };

    const getPluralUnitLabel = (label) => {
        const plurals = {
            'Piece': 'Pieces',
            'Dozen': 'Dozens',
            'Box': 'Boxes',
            'Packet': 'Packets',
            'Can': 'Cans',
            'Bottle': 'Bottles',
            'Unit': 'Units'
        };
        return plurals[label] || label + 's';
    };

    const calculateBQuantityString = (itemsPerPack, unitOfMeasure, totalQuantity) => {
        if (!itemsPerPack || itemsPerPack <= 0 || !totalQuantity || totalQuantity <= 0 || !unitOfMeasure) {
            return '';
        }
        
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const totalItems = itemsPerPack * totalQuantity;
        const bulkValue = totalItems * unitValue;
        const unitLabel = getUnitDisplayLabel(unitOfMeasure);
        
        let formattedValue;
        if (Math.floor(bulkValue) === bulkValue) {
            formattedValue = bulkValue.toFixed(0);
        } else {
            formattedValue = bulkValue.toFixed(2);
        }
        
        const finalLabel = bulkValue !== 1 ? getPluralUnitLabel(unitLabel) : unitLabel;
        
        return `${formattedValue} ${finalLabel}`;
    };

    const calculateCostPerUnitNumeric = (costPerPack, itemsPerPack, unitOfMeasure) => {
        if (!costPerPack || costPerPack <= 0 || !itemsPerPack || itemsPerPack <= 0) {
            return 0;
        }
        const perUnit = costPerPack / itemsPerPack;
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const costPerBaseUnit = perUnit / unitValue;
        return costPerBaseUnit;
    };

    const calculateSellPerPackFromUnit = (sellPricePerUnit, itemsPerPack, unitOfMeasure) => {
        if (!sellPricePerUnit || sellPricePerUnit <= 0 || !itemsPerPack || itemsPerPack <= 0 || !unitOfMeasure) {
            return 0;
        }
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const perUnitPrice = sellPricePerUnit * unitValue;
        const sellPerPack = perUnitPrice * itemsPerPack;
        return sellPerPack;
    };

    const formatPrice = (value) => {
        if (value === 0) return '';
        if (value < 0.01) return value.toFixed(4);
        if (value < 1) return value.toFixed(3);
        return value.toFixed(2);
    };

    // ==============================================
    // FILTERED OPTIONS
    // ==============================================
    const filteredCategories = categorySearchTerm.trim() === '' 
        ? categoryOptions 
        : categoryOptions.filter(cat => 
            cat.label.toLowerCase().includes(categorySearchTerm.toLowerCase())
          );

    const filteredColors = colorSearchTerm.trim() === '' 
        ? colorOptions.filter(c => c.value !== '')
        : colorOptions.filter(c => 
            c.value !== '' && c.label.toLowerCase().includes(colorSearchTerm.toLowerCase())
          );

    const filteredSizes = sizeSearchTerm.trim() === ''
        ? sizeOptions.filter(s => s.value !== '')
        : sizeOptions.filter(s => 
            s.value !== '' && s.label.toLowerCase().includes(sizeSearchTerm.toLowerCase())
          );

    const filteredUnits = unitSearchTerm.trim() === '' 
        ? unitOptions 
        : unitOptions.filter(u => 
            u.value !== '' && u.label.toLowerCase().includes(unitSearchTerm.toLowerCase())
          );

    const filteredSuppliers = supplierSearchTerm.trim() === ''
        ? suppliers
        : suppliers.filter(s => 
            s.supplier_name?.toLowerCase().includes(supplierSearchTerm.toLowerCase()) ||
            s.company?.toLowerCase().includes(supplierSearchTerm.toLowerCase()) ||
            s.phone?.toLowerCase().includes(supplierSearchTerm.toLowerCase()) ||
            s.contact_person?.toLowerCase().includes(supplierSearchTerm.toLowerCase())
          );

    const filteredGauges = gaugeSearchTerm.trim() === '' 
        ? gaugeOptions 
        : gaugeOptions.filter(g => 
            g.value !== '' && g.label.toLowerCase().includes(gaugeSearchTerm.toLowerCase())
          );

    const filteredWeights = weightSearchTerm.trim() === '' 
        ? weightOptions 
        : weightOptions.filter(w => 
            w.value !== '' && w.label.toLowerCase().includes(weightSearchTerm.toLowerCase())
          );

    const filteredClasses = classSearchTerm.trim() === '' 
        ? classOptions 
        : classOptions.filter(c => 
            c.value !== '' && c.label.toLowerCase().includes(classSearchTerm.toLowerCase())
          );

    // ==============================================
    // SHOW TOAST FUNCTION
    // ==============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ==============================================
    // FETCH SUPPLIERS FUNCTION
    // ==============================================
    const fetchSuppliers = useCallback(async (searchTerm = '') => {
        if (!currentShop?.shop_id) {
            return;
        }

        setLoadingSuppliers(true);
        try {
            const params = new URLSearchParams({
                action: 'search_suppliers',
                shop_id: currentShop.shop_id,
                language: currentLanguage
            });
            if (searchTerm) {
                params.append('searchTerm', searchTerm);
            }

            const response = await fetch(`https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php?${params}`);
            const data = await response.json();

            if (data.status === 'success' && data.data) {
                setSuppliers(data.data);
            } else {
                setSuppliers([]);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            setSuppliers([]);
        } finally {
            setLoadingSuppliers(false);
        }
    }, [currentShop, currentLanguage]);

    // ==============================================
    // CENTRALIZED SHOPS FUNCTIONS
    // ==============================================
    const fetchAllShopsForCentralized = async (search = '') => {
        try {
            const params = new URLSearchParams({
                action: 'get_all_shops',
                current_user_id: user?.id || '',
                current_user_role: user?.role || ''
            });
            if (search) params.append('search', search);

            const response = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?${params}`);
            const data = await response.json();

            if (data.success) {
                setAllAvailableShopsForCentralized(data.data);
            }
        } catch (error) {
            console.error('Error fetching shops for centralized:', error);
        }
    };

    const addCentralizedShop = (shop) => {
        if (!shop) return;

        const assignedShopIds = centralizedShopIds.map(id => parseInt(id));
        if (assignedShopIds.includes(shop.shop_id)) {
            showToast('warning', 'This shop is already assigned as centralized');
            setShowCentralizedShopDropdown(false);
            setCentralizedShopSearchTerm('');
            return;
        }

        setCentralizedShopIds(prev => [...prev, shop.shop_id]);
        setFormData(prev => ({
            ...prev,
            centralized_shops: [...prev.centralized_shops, shop.shop_id]
        }));
        setShowCentralizedShopDropdown(false);
        setCentralizedShopSearchTerm('');
    };

    const removeCentralizedShop = (shopId) => {
        setCentralizedShopIds(prev => prev.filter(id => id !== shopId));
        setFormData(prev => ({
            ...prev,
            centralized_shops: prev.centralized_shops.filter(id => id !== shopId)
        }));
    };

    const handleCentralizedShopSearchChange = (e) => {
        const value = e.target.value;
        setCentralizedShopSearchTerm(value);
        fetchAllShopsForCentralized(value);
        setShowCentralizedShopDropdown(true);
    };

    // ==============================================
    // SELECT HANDLERS
    // ==============================================
    const selectCategory = (category) => {
        setFormData(prev => ({ ...prev, category: category.value }));
        setCategorySearchTerm(category.label);
        setShowCategoryDropdown(false);
    };

    const selectColor = (color) => {
        setFormData(prev => ({ ...prev, color: color.value }));
        setColorSearchTerm(color.label);
        setShowColorDropdown(false);
    };

    const selectSize = (size) => {
        setFormData(prev => ({ ...prev, size: size.value }));
        setSizeSearchTerm(size.label);
        setShowSizeDropdown(false);
    };

    const selectUnit = (unit) => {
        setFormData(prev => ({ ...prev, gunit: unit.value }));
        setUnitSearchTerm(unit.label);
        setShowUnitDropdown(false);
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({
            ...prev,
            supplier_id: supplier.supplierid,
            supplier_name: sourceValue(supplier, 'supplier_name')
        }));
        setSupplierSearchTerm(supplier.supplier_name);
        setShowSupplierDropdown(false);
    };

    const selectGauge = (gauge) => {
        setFormData(prev => ({ ...prev, gauge: gauge.value }));
        setGaugeSearchTerm(gauge.label);
        setShowGaugeDropdown(false);
    };

    const selectWeight = (weight) => {
        setFormData(prev => ({ ...prev, weight: weight.value }));
        setWeightSearchTerm(weight.label);
        setShowWeightDropdown(false);
    };

    const selectClass = (classOpt) => {
        setFormData(prev => ({ ...prev, class: classOpt.value }));
        setClassSearchTerm(classOpt.label);
        setShowClassDropdown(false);
    };

    // ==============================================
    // INPUT CHANGE HANDLERS
    // ==============================================
    const handleSupplierSearchChange = (e) => {
        const value = e.target.value;
        setSupplierSearchTerm(value);
        setFormData(prev => ({
            ...prev,
            supplier_id: '',
            supplier_name: value
        }));
        setShowSupplierDropdown(true);
        fetchSuppliers(value);
    };

    // ==============================================
    // HANDLE BARCODE SCAN
    // ==============================================
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (isScanningRef.current) return;
        if (!barcode || barcode.trim() === '') return;
        if (!currentShop?.shop_id) {
            showToast('warning', translations.selectShop || 'Please select a shop first');
            return;
        }

        isScanningRef.current = true;
        setLastScannedBarcode(barcode);

        try {
            const response = await fetch(PRODUCTS_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record_by_barcode',
                    barcode: barcode,
                    username: user?.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });

            const data = await response.json();

            if (data.status === 'success' && data.data) {
                const product = data.data;
                
                if (isEditMode && formData.product_name) {
                    if (!window.confirm('Loading this product will replace current form data. Continue?')) {
                        isScanningRef.current = false;
                        return;
                    }
                }

                const isCentralized = product.is_product_centralized === 1;
                const centralizedShops = product.centralized_shops || [];
                
                setCentralizedShopIds(centralizedShops);
                setIsProductCentralized(isCentralized);

                setFormData(prev => ({
                    ...prev,
                    date: product.date || new Date().toISOString().split('T')[0],
                    batch_no: product.batch_no || '',
                    barcode: product.barcode || '',
                    product_name: product.product_name || '',
                    warranty: product.warranty || '',
                    product_code: product.product_code || '',
                    category: product.category || '',
                    company: product.company || '',
                    color: product.color || '',
                    size: product.size || '',
                    gauge: product.gauge || '',
                    weight: product.weight || '',
                    class: product.class || '',
                    gunit: product.gunit || '',
                    supplier_id: product.supplier_id || '',
                    supplier_name: product.supplier_name || '',
                    packing_type: product.packing_type || 'packet',
                    sub_packing_type: product.sub_packing_type || 'piece',
                    items_per_pack: product.items_per_pack || '1',
                    unit_of_measure: product.unit_of_measure || 'piece',
                    cost_price_per_pack: product.cost_price_per_pack || product.cost_per_pack || '',
                    sell_price_per_unit: product.sell_price_per_unit || '',
                    total_quantity: product.total_quantity || product.quantity || '',
                    quantity_alert: product.quantity_alert || '2',
                    expiry_date: product.expiry_date || '',
                    cost_per_unit: product.cost_per_unit || '',
                    sell_per_pack: product.sell_per_pack || '',
                    bquantity: product.bquantity || '',
                    batch_no_manual: true,
                    barcode_manual: true,
                    existing_batch_nos: prev.existing_batch_nos || [],
                    is_product_centralized: isCentralized ? 1 : 0,
                    centralized_shops: centralizedShops
                }));

                const selectedCategory = categoryOptions.find(c => c.value === product.category);
                if (selectedCategory) setCategorySearchTerm(selectedCategory.label);
                
                const selectedColor = colorOptions.find(c => c.value === product.color);
                if (selectedColor) setColorSearchTerm(selectedColor.label);
                
                const selectedSize = sizeOptions.find(s => s.value === product.size);
                if (selectedSize) setSizeSearchTerm(selectedSize.label);

                if (product.gunit) {
                    const selectedUnit = unitOptions.find(u => u.value === product.gunit);
                    setUnitSearchTerm(selectedUnit ? selectedUnit.label : product.gunit);
                }

                if (product.supplier_name) {
                    setSupplierSearchTerm(product.supplier_name);
                }

                if (product.gauge) {
                    const selectedGauge = gaugeOptions.find(g => g.value === product.gauge);
                    setGaugeSearchTerm(selectedGauge ? selectedGauge.label : product.gauge);
                }

                if (product.weight) {
                    const selectedWeight = weightOptions.find(w => w.value === product.weight);
                    setWeightSearchTerm(selectedWeight ? selectedWeight.label : product.weight);
                }

                if (product.class) {
                    const selectedClass = classOptions.find(c => c.value === product.class);
                    setClassSearchTerm(selectedClass ? selectedClass.label : product.class);
                }

                setIsEditMode(true);
                
                if (product.cost_per_unit) setCalculatedCostPerUnit(product.cost_per_unit);
                if (product.sell_per_pack) setCalculatedSellPerPack(product.sell_per_pack);
                if (product.bquantity) setCalculatedBQuantity(product.bquantity);

                showToast('success', `${translations.productFound || 'Product found!'} ${product.product_name}`);

            } else {
                if (!isEditMode && !formData.barcode) {
                    const searchResponse = await fetch(PRODUCTS_API_URL, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'search_products',
                            searchTerm: barcode,
                            username: user?.username,
                            shop_id: currentShop.shop_id,
                            language: currentLanguage
                        })
                    });

                    const searchData = await searchResponse.json();
                    
                    if (searchData.status === 'success' && searchData.data && searchData.data.length > 0) {
                        const product = searchData.data[0];
                        const isCentralized = product.is_product_centralized === 1;
                        const centralizedShops = product.centralized_shops || [];
                        
                        setCentralizedShopIds(centralizedShops);
                        setIsProductCentralized(isCentralized);
                        
                        setFormData(prev => ({
                            ...prev,
                            date: product.date || new Date().toISOString().split('T')[0],
                            batch_no: product.batch_no || '',
                            barcode: product.barcode || '',
                            product_name: product.product_name || '',
                            warranty: product.warranty || '',
                            product_code: product.product_code || '',
                            category: product.category || '',
                            company: product.company || '',
                            color: product.color || '',
                            size: product.size || '',
                            gauge: product.gauge || '',
                            weight: product.weight || '',
                            class: product.class || '',
                            gunit: product.gunit || '',
                            supplier_id: product.supplier_id || '',
                            supplier_name: product.supplier_name || '',
                            packing_type: product.packing_type || 'packet',
                            sub_packing_type: product.sub_packing_type || 'piece',
                            items_per_pack: product.items_per_pack || '1',
                            unit_of_measure: product.unit_of_measure || 'piece',
                            cost_price_per_pack: product.cost_price_per_pack || product.cost_per_pack || '',
                            sell_price_per_unit: product.sell_price_per_unit || '',
                            total_quantity: product.total_quantity || product.quantity || '',
                            quantity_alert: product.quantity_alert || '2',
                            expiry_date: product.expiry_date || '',
                            cost_per_unit: product.cost_per_unit || '',
                            sell_per_pack: product.sell_per_pack || '',
                            bquantity: product.bquantity || '',
                            batch_no_manual: true,
                            barcode_manual: true,
                            existing_batch_nos: prev.existing_batch_nos || [],
                            is_product_centralized: isCentralized ? 1 : 0,
                            centralized_shops: centralizedShops
                        }));
                        setIsEditMode(true);
                        showToast('success', `${translations.productFound || 'Product found!'} ${product.product_name}`);
                    } else {
                        setFormData(prev => ({ 
                            ...prev, 
                            barcode: barcode,
                            barcode_manual: true 
                        }));
                        showToast('info', `${translations.barcodeSet || 'Barcode set'}: ${barcode}`);
                        
                        setTimeout(() => {
                            if (productNameRef.current) {
                                productNameRef.current.focus();
                            }
                        }, 300);
                    }
                } else {
                    showToast('warning', `${translations.productNotFound || 'Product not found'}: ${barcode}`);
                }
            }
        } catch (error) {
            console.error('Barcode scan error:', error);
            showToast('error', translations.networkError || 'Error processing barcode scan');
        } finally {
            isScanningRef.current = false;
        }
    }, [currentShop, user?.username, isEditMode, formData.barcode, formData.product_name, translations, showToast]);

    // ==============================================
    // INITIALIZE BARCODE SCANNER
    // ==============================================
    useBarcodeScanner(handleBarcodeScan, isScannerActive);

    // ==============================================
    // EFFECTS
    // ==============================================
    
    // Click outside handlers for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
            if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
                setShowColorDropdown(false);
            }
            if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target)) {
                setShowSizeDropdown(false);
            }
            if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
                setShowUnitDropdown(false);
            }
            if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
                setShowSupplierDropdown(false);
            }
            if (gaugeDropdownRef.current && !gaugeDropdownRef.current.contains(event.target)) {
                setShowGaugeDropdown(false);
            }
            if (weightDropdownRef.current && !weightDropdownRef.current.contains(event.target)) {
                setShowWeightDropdown(false);
            }
            if (classDropdownRef.current && !classDropdownRef.current.contains(event.target)) {
                setShowClassDropdown(false);
            }
            if (centralizedDropdownRef && centralizedDropdownRef.current && !centralizedDropdownRef.current.contains(event.target)) {
                setShowCentralizedShopDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Tab key handler
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowCategoryDropdown(false);
                setShowColorDropdown(false);
                setShowSizeDropdown(false);
                setShowUnitDropdown(false);
                setShowSupplierDropdown(false);
                setShowGaugeDropdown(false);
                setShowWeightDropdown(false);
                setShowClassDropdown(false);
                setShowCentralizedShopDropdown(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus
    useEffect(() => {
        setTimeout(() => {
            if (productNameRef.current) {
                productNameRef.current.focus();
            }
        }, 100);
    }, []);

    // Fetch suppliers when shop changes
    useEffect(() => {
        if (currentShop?.shop_id) {
            fetchSuppliers();
        }
    }, [currentShop, fetchSuppliers]);

    // Calculate fields
    useEffect(() => {
        let newCostPerUnit = '';
        let newSellPerPack = '';
        let newBQuantity = '';
        
        if (formData.cost_price_per_pack && formData.items_per_pack && formData.unit_of_measure) {
            const cpuNumeric = calculateCostPerUnitNumeric(
                parseFloat(formData.cost_price_per_pack),
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure
            );
            newCostPerUnit = formatPrice(cpuNumeric);
            setCalculatedCostPerUnit(newCostPerUnit);
        } else {
            setCalculatedCostPerUnit('');
        }
        
        if (formData.sell_price_per_unit && formData.items_per_pack && formData.unit_of_measure) {
            const spPackNumeric = calculateSellPerPackFromUnit(
                parseFloat(formData.sell_price_per_unit),
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure
            );
            newSellPerPack = formatPrice(spPackNumeric);
            setCalculatedSellPerPack(newSellPerPack);
        } else {
            setCalculatedSellPerPack('');
        }
        
        if (formData.items_per_pack && formData.unit_of_measure && formData.total_quantity) {
            newBQuantity = calculateBQuantityString(
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure,
                parseFloat(formData.total_quantity)
            );
            setCalculatedBQuantity(newBQuantity);
        } else {
            setCalculatedBQuantity('');
        }
        
        setFormData(prev => ({
            ...prev,
            cost_per_unit: newCostPerUnit,
            sell_per_pack: newSellPerPack,
            bquantity: newBQuantity
        }));
    }, [formData.cost_price_per_pack, formData.sell_price_per_unit, formData.items_per_pack, formData.unit_of_measure, formData.total_quantity]);

    // Load existing batch numbers
    useEffect(() => {
        const loadExistingBatchNumbers = async () => {
            if (!currentShop) return;
            
            try {
                const response = await fetch(PRODUCTS_API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_all_products',
                        username: user?.username,
                        shop_id: currentShop.shop_id,
                        language: currentLanguage
                    })
                });
                
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    const batchNos = data.data
                        .map(p => p.batch_no)
                        .filter(b => b && b.length >= 7);
                    setFormData(prev => ({ ...prev, existing_batch_nos: batchNos }));
                }
            } catch (error) {
                console.error('Error loading existing batch numbers:', error);
            }
        };
        
        loadExistingBatchNumbers();
    }, [currentShop, user?.username]);

    // Auto-generate batch number
    useEffect(() => {
        if (!isEditMode && 
            formData.product_name && 
            formData.product_name.trim() !== '' && 
            !loading && 
            !isClearingRef.current &&
            !formData.batch_no_manual) {
            
            const newBatchNo = generateBatchNoFromProductName(
                formData.product_name,
                formData.existing_batch_nos || []
            );
            
            if (formData.batch_no !== newBatchNo) {
                setFormData(prev => ({ ...prev, batch_no: newBatchNo }));
                const message = (translations.batchAutoGenerated || 'Batch number auto-generated: ') + newBatchNo;
                showToast('info', message);
            }
        }
    }, [formData.product_name, isEditMode, loading, translations, formData.existing_batch_nos, showToast]);

    // Auto-generate barcode
    useEffect(() => {
        if (!isEditMode && 
            formData.product_name && 
            formData.product_name.trim() !== '' && 
            !loading && 
            !isClearingRef.current &&
            !formData.barcode_manual &&
            user?.username && 
            currentShop?.shop_id) {
            
            const newBarcode = generateBarcodeFromProductName(
                formData.product_name,
                user.username,
                currentShop.shop_id
            );
            
            if (newBarcode && formData.barcode !== newBarcode) {
                setFormData(prev => ({ ...prev, barcode: newBarcode }));
            }
        }
    }, [formData.product_name, formData.company, isEditMode, loading, user?.username, currentShop?.shop_id]);

    // Clear batch/barcode if product name cleared
    useEffect(() => {
        if (!isEditMode && (!formData.product_name || formData.product_name.trim() === '') && !loading) {
            if (formData.batch_no !== '' && !formData.batch_no_manual) {
                setFormData(prev => ({ ...prev, batch_no: '' }));
            }
            if (formData.barcode !== '' && !formData.barcode_manual) {
                setFormData(prev => ({ ...prev, barcode: '' }));
            }
        }
    }, [formData.product_name, isEditMode, loading]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // Language sync
    useEffect(() => {
        if (user?.language && user.language !== currentLanguage) {
            setCurrentLanguage(user.language);
        }
    }, [user?.language]);

    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await productsTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Load product if editing
    useEffect(() => {
        if (productId) {
            setIsEditMode(true);
            loadProductData(productId);
        }
    }, [productId]);

    // Load shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // ==============================================
    // VALIDATE FORM
    // ==============================================
    const validateForm = () => {
        const newErrors = {};

        if (!formData.product_name?.trim()) {
            newErrors.product_name = translations.nameRequired || 'Product name is required';
        } else if (formData.product_name.trim().length < 2) {
            newErrors.product_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (!formData.cost_price_per_pack || parseFloat(formData.cost_price_per_pack) <= 0) {
            newErrors.cost_price_per_pack = translations.costPriceRequired || 'Cost price is required';
        }

        if (!formData.sell_price_per_unit || parseFloat(formData.sell_price_per_unit) <= 0) {
            newErrors.sell_price_per_unit = translations.sellingPriceRequired || 'Selling price is required';
        }

        if (!formData.total_quantity || parseFloat(formData.total_quantity) < 0) {
            newErrors.total_quantity = translations.quantityRequired || 'Quantity is required';
        }

        if (formData.expiry_date) {
            const expiry = new Date(formData.expiry_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (expiry < today) {
                newErrors.expiry_date = translations.expiryPast || 'Expiry date cannot be in the past';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ==============================================
    // HANDLE SUBMIT
    // ==============================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        if (!isEditMode && formData.product_name && formData.product_name.trim() !== '' && (!formData.batch_no || formData.batch_no.trim() === '')) {
            const newBatchNo = generateBatchNoFromProductName(
                formData.product_name,
                formData.existing_batch_nos || []
            );
            setFormData(prev => ({ ...prev, batch_no: newBatchNo }));
            await new Promise(resolve => setTimeout(resolve, 50));
        }

        if (!isEditMode && formData.product_name && formData.product_name.trim() !== '' && (!formData.barcode || formData.barcode.trim() === '')) {
            const newBarcode = generateBarcodeFromProductName(
                formData.product_name,
                user?.username,
                currentShop?.shop_id
            );
            if (newBarcode) {
                setFormData(prev => ({ ...prev, barcode: newBarcode }));
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        }

        if (!validateForm()) return;

        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        if (now - lastSubmitTimeRef.current < 3000) {
            showToast('warning', 'Please wait before submitting again');
            return;
        }

        const currentProductData = JSON.stringify({
            product_name: formData.product_name,
            batch_no: formData.batch_no,
            barcode: formData.barcode,
            product_code: formData.product_code,
            shop_id: currentShop.shop_id
        });

        if (!isEditMode && currentProductData === lastProductDataRef.current) {
            showToast('warning', 'This product appears to be a duplicate. Please check.');
            return;
        }

        if (!isEditMode && isDuplicateRequest({
            action: 'add',
            product_name: formData.product_name,
            batch_no: formData.batch_no,
            barcode: formData.barcode,
            product_code: formData.product_code,
            shop_id: currentShop.shop_id
        })) {
            showToast('warning', 'Please wait, a similar product is being added...');
            return;
        }

        lastProductDataRef.current = currentProductData;
        lastSubmitTimeRef.current = now;

        isSubmittingRef.current = true;
        setLoading(true);

        const submitButton = document.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
            const currentUsername = user?.username || 'Unknown';
            
            const productData = {
                action: isEditMode ? 'edit' : 'add',
                product_name: formData.product_name,
                warranty: formData.warranty?.trim() || '',
                product_code: formData.product_code,
                batch_no: formData.batch_no || '',
                barcode: formData.barcode || '',
                category: formData.category || '',
                company: formData.company || '',
                color: formData.color || '',
                size: formData.size || '',
                gauge: formData.gauge || '',
                weight: formData.weight || '',
                class: formData.class || '',
                gunit: formData.gunit || '',
                supplier_id: formData.supplier_id || null,
                supplier_name: formData.supplier_name || '',
                date: formData.date,
                expiry_date: formData.expiry_date || null,
                quantity_alert: '2',
                total_quantity: formData.total_quantity,
                packing_type: 'packet',
                sub_packing_type: 'piece',
                items_per_pack: 1,
                unit_of_measure: 'piece',
                cost_price_per_pack: formData.cost_price_per_pack,
                sell_price_per_unit: formData.sell_price_per_unit,
                purchase_type: 'packing',
                username: currentUsername,
                shop_id: currentShop.shop_id,
                language: currentLanguage,
                original_language: formData.original_language || currentLanguage,
                is_product_centralized: isProductCentralized ? 1 : 0,
                centralized_shops: isProductCentralized ? centralizedShopIds : [],
                _timestamp: Date.now()
            };

            if (isEditMode && productId) {
                productData.id = productId;
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(PRODUCTS_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(productData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server responded with status ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server. Please check the server logs.');
            }

            if (data.status === 'duplicate' || data.duplicate_prevention) {
                showToast('warning', data.message || 'Duplicate submission prevented. Please wait.');
                lastProductDataRef.current = '';
                return;
            }

            if (data.status === 'success') {
                showToast('success', isEditMode
                    ? (translations.productUpdated || 'Product updated successfully!')
                    : (translations.productSaved || 'Product saved successfully!'));

                if (!isEditMode && formData.batch_no) {
                    setFormData(prev => ({
                        ...prev,
                        existing_batch_nos: [...prev.existing_batch_nos, formData.batch_no]
                    }));
                }

                if (!isEditMode) {
                    setFormData(prev => ({
                        date: new Date().toISOString().split('T')[0],
                        batch_no: '',
                        barcode: '',
                        product_name: '',
                        warranty: '',
                        product_code: '',
                        category: '',
                        company: '',
                        color: '',
                        size: '',
                        gauge: '',
                        weight: '',
                        class: '',
                        gunit: '',
                        supplier_id: '',
                        supplier_name: '',
                        packing_type: 'packet',
                        sub_packing_type: 'piece',
                        items_per_pack: '1',
                        unit_of_measure: 'piece',
                        cost_price_per_pack: '',
                        sell_price_per_unit: '',
                        total_quantity: '',
                        quantity_alert: '2',
                        expiry_date: '',
                        cost_per_unit: '',
                        sell_per_pack: '',
                        bquantity: '',
                        purchase_type: 'packing',
                        themeColor: prev.themeColor,
                        batch_no_manual: false,
                        barcode_manual: false,
                        existing_batch_nos: prev.existing_batch_nos,
                        is_product_centralized: 0,
                        centralized_shops: []
                    }));
                    setCategorySearchTerm('');
                    setColorSearchTerm('');
                    setSizeSearchTerm('');
                    setUnitSearchTerm('');
                    setSupplierSearchTerm('');
                    setGaugeSearchTerm('');
                    setWeightSearchTerm('');
                    setClassSearchTerm('');
                    setCentralizedShopIds([]);
                    setIsProductCentralized(false);
                    setErrors({});
                    lastProductDataRef.current = '';
                    setCalculatedCostPerUnit('');
                    setCalculatedSellPerPack('');
                    setCalculatedBQuantity('');
                    
                    setTimeout(() => {
                        if (productNameRef.current) {
                            productNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.productid || productId);
            } else {
                showToast('error', data.message || (isEditMode
                    ? translations.failedUpdate
                    : translations.failedAdd) || 'Operation failed');
                lastProductDataRef.current = '';
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                showToast('error', 'Request timeout. Please try again.');
            } else {
                console.error('Submit error:', error);
                showToast('error', error.message || translations.networkError || 'Network error');
            }
            lastProductDataRef.current = '';
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
                if (submitButton) submitButton.disabled = false;
            }, 3000);
        }
    };

    // ==============================================
    // HANDLE CHANGE
    // ==============================================
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'total_quantity') {
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (name === 'sell_price_per_unit') {
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (name === 'cost_per_unit' || name === 'sell_per_pack' || name === 'bquantity') {
            return;
        } else if (name === 'product_name') {
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                batch_no_manual: prev.batch_no ? prev.batch_no_manual : false,
                barcode_manual: prev.barcode ? prev.barcode_manual : false
            }));
        } else if (name === 'batch_no') {
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                batch_no_manual: true
            }));
        } else if (name === 'barcode') {
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                barcode_manual: true
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // ==============================================
    // HANDLE CLEAR
    // ==============================================
    const handleClear = () => {
        isClearingRef.current = true;

        setFormData({
            date: new Date().toISOString().split('T')[0],
            batch_no: '',
            barcode: '',
            product_name: '',
            warranty: '',
            product_code: '',
            category: '',
            company: '',
            color: '',
            size: '',
            gauge: '',
            weight: '',
            class: '',
            gunit: '',
            supplier_id: '',
            supplier_name: '',
            packing_type: 'packet',
            sub_packing_type: 'piece',
            items_per_pack: '1',
            unit_of_measure: 'piece',
            cost_price_per_pack: '',
            sell_price_per_unit: '',
            total_quantity: '',
            quantity_alert: '2',
            expiry_date: '',
            cost_per_unit: '',
            sell_per_pack: '',
            bquantity: '',
            purchase_type: 'packing',
            themeColor: formData.themeColor,
            batch_no_manual: false,
            barcode_manual: false,
            existing_batch_nos: formData.existing_batch_nos || [],
            is_product_centralized: 0,
            centralized_shops: []
        });
        setCategorySearchTerm('');
        setColorSearchTerm('');
        setSizeSearchTerm('');
        setUnitSearchTerm('');
        setSupplierSearchTerm('');
        setGaugeSearchTerm('');
        setWeightSearchTerm('');
        setClassSearchTerm('');
        setCentralizedShopIds([]);
        setIsProductCentralized(false);
        setErrors({});
        setIsEditMode(false);
        lastProductDataRef.current = '';
        setCalculatedCostPerUnit('');
        setCalculatedSellPerPack('');
        setCalculatedBQuantity('');

        showToast('success', translations.draftCleared || 'Form cleared');

        setTimeout(() => {
            if (productNameRef.current) {
                productNameRef.current.focus();
            }
        }, 100);

        setTimeout(() => {
            isClearingRef.current = false;
        }, 500);
    };

    // ==============================================
    // HANDLE SWITCH SHOP
    // ==============================================
    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                setCalculatedCostPerUnit('');
                setCalculatedSellPerPack('');
                setCalculatedBQuantity('');
                setCategorySearchTerm('');
                setColorSearchTerm('');
                setSizeSearchTerm('');
                setUnitSearchTerm('');
                setSupplierSearchTerm('');
                setGaugeSearchTerm('');
                setWeightSearchTerm('');
                setClassSearchTerm('');
                setSuppliers([]);
                setCentralizedShopIds([]);
                setIsProductCentralized(false);
                setFormData(prev => ({ 
                    ...prev, 
                    existing_batch_nos: [],
                    is_product_centralized: 0,
                    centralized_shops: [],
                    supplier_id: '',
                    supplier_name: '',
                    gauge: '',
                    weight: '',
                    class: ''
                }));
                
                fetchSuppliers();
                
                const loadExistingBatchNumbers = async () => {
                    try {
                        const response = await fetch(PRODUCTS_API_URL, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_all_products',
                                username: user?.username,
                                shop_id: shop.shop_id,
                                language: currentLanguage
                            })
                        });
                        const data = await response.json();
                        if (data.status === 'success' && data.data) {
                            const batchNos = data.data
                                .map(p => p.batch_no)
                                .filter(b => b && b.length >= 7);
                            setFormData(prev => ({ ...prev, existing_batch_nos: batchNos }));
                        }
                    } catch (error) {
                        console.error('Error loading existing batch numbers:', error);
                    }
                };
                loadExistingBatchNumbers();
            }
            showToast(success ? 'success' : 'error',
                success ? (translations.shopSwitched || 'Shop switched successfully')
                    : (translations.shopSwitchFailed || 'Failed to switch shop'));
        } catch (error) {
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // ==============================================
    // LOAD PRODUCT DATA
    // ==============================================
    const loadProductData = async (id) => {
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(PRODUCTS_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    username: user?.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success' && data.data) {
                const product = data.data;
                const isCentralized = product.is_product_centralized === 1;
                const centralizedShops = product.centralized_shops || [];
                
                setCentralizedShopIds(centralizedShops);
                setIsProductCentralized(isCentralized);
                
                setFormData(prev => ({
                    ...prev,
                    date: product.date || new Date().toISOString().split('T')[0],
                    batch_no: product.batch_no || '',
                    barcode: product.barcode || '',
                    product_name: sourceValue(product, 'product_name'),
                    warranty: sourceValue(product, 'warranty'),
                    product_code: product.product_code || '',
                    category: sourceValue(product, 'category'),
                    company: sourceValue(product, 'company'),
                    color: sourceValue(product, 'color'),
                    size: sourceValue(product, 'size'),
                    gauge: product.gauge || '',
                    weight: product.weight || '',
                    class: sourceValue(product, 'class'),
                    gunit: product.gunit || '',
                    supplier_id: product.supplier_id || '',
                    supplier_name: sourceValue(product, 'supplier_name'),
                    original_language: product.original_language || currentLanguage,
                    packing_type: product.packing_type || 'packet',
                    sub_packing_type: product.sub_packing_type || 'piece',
                    items_per_pack: product.items_per_pack || '1',
                    unit_of_measure: product.unit_of_measure || 'piece',
                    cost_price_per_pack: product.cost_per_pack || '',
                    sell_price_per_unit: product.sell_price_per_unit || '',
                    total_quantity: product.quantity || '',
                    quantity_alert: product.quantity_alert || '2',
                    expiry_date: product.expiry_date || '',
                    cost_per_unit: product.cost_per_unit || '',
                    sell_per_pack: product.sell_per_pack || '',
                    bquantity: product.bquantity || '',
                    themeColor: prev.themeColor,
                    batch_no_manual: true,
                    barcode_manual: true,
                    is_product_centralized: isCentralized ? 1 : 0,
                    centralized_shops: centralizedShops
                }));
                
                const selectedCategory = categoryOptions.find(c => c.value === product.category);
                setCategorySearchTerm(selectedCategory ? selectedCategory.label : '');
                const selectedColor = colorOptions.find(c => c.value === product.color);
                setColorSearchTerm(selectedColor ? selectedColor.label : '');
                const selectedSize = sizeOptions.find(s => s.value === product.size);
                setSizeSearchTerm(selectedSize ? selectedSize.label : '');
                
                if (product.gunit) {
                    const selectedUnit = unitOptions.find(u => u.value === product.gunit);
                    setUnitSearchTerm(selectedUnit ? selectedUnit.label : product.gunit);
                }
                
                if (product.supplier_name) {
                    setSupplierSearchTerm(product.supplier_name);
                }

                if (product.gauge) {
                    const selectedGauge = gaugeOptions.find(g => g.value === product.gauge);
                    setGaugeSearchTerm(selectedGauge ? selectedGauge.label : product.gauge);
                }

                if (product.weight) {
                    const selectedWeight = weightOptions.find(w => w.value === product.weight);
                    setWeightSearchTerm(selectedWeight ? selectedWeight.label : product.weight);
                }

                if (product.class) {
                    const selectedClass = classOptions.find(c => c.value === product.class);
                    setClassSearchTerm(selectedClass ? selectedClass.label : product.class);
                }
                
                if (product.cost_per_unit) setCalculatedCostPerUnit(product.cost_per_unit);
                if (product.sell_per_pack) setCalculatedSellPerPack(product.sell_per_pack);
                if (product.bquantity) setCalculatedBQuantity(product.bquantity);
            } else {
                showToast('error', data.message || 'Failed to load product data');
            }
        } catch (error) {
            console.error('Load product error:', error);
            showToast('error', translations.networkError || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // THEME AND DARK MODE EFFECTS
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('dynamic-orb-styles-product');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-product';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
        }, 500);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentThemeColor]);

    // Dark mode
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
                
                if (isDark) {
                    document.body.classList.add('ozui-dark');
                    document.documentElement.classList.add('ozui-dark');
                } else {
                    document.body.classList.remove('ozui-dark');
                    document.documentElement.classList.remove('ozui-dark');
                }
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // Language
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await productsTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    const isRTL = RTL_LANGUAGE_CODES.has(currentLanguage);

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozui-shop-indicator no-shop">
                    <div className="ozui-shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="ozui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="ozui-header-shop-btn"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        
        return (
            <div className="ozui-shop-indicator">
                {/* Shop indicator content */}
            </div>
        );
    };

    return (
        <div
            className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            lang={currentLanguage}
        >
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Hidden Barcode Input for scanner focus */}
            <input
                ref={barcodeInputRef}
                type="text"
                id="barcode_scanner_hidden"
                name="barcode_scanner_hidden"
                style={{ 
                    position: 'absolute', 
                    left: '-9999px', 
                    opacity: 0, 
                    width: '1px', 
                    height: '1px',
                    pointerEvents: 'none'
                }}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                onChange={() => {}}
            />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations.editProduct || 'Edit Stock') : (translations.addProduct || 'Add Stock')}</h1>
                            <p>{translations.fillDetails || 'Fill in the stock details below'}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        {renderShopIndicator()}
                        
                        <div className="ozui-header-actions">
                            <button 
                                className="ozui-action-btn clear-draft" 
                                onClick={handleClear}
                                disabled={loading}
                                title={translations.clearButton || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations.clearButton || 'Clear'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    {/* Date */}
                    <div className={`ozui-field ${errors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations.date || 'Date'}
                            </label>
                        </div>
                    </div>

                    {/* Product Name */}
                    <div className={`ozui-field ${errors.product_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={productNameRef}
                                type="text"
                                name="product_name"
                                id="product_name"
                                value={formData.product_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="product_name">
                                <FontAwesomeIcon icon={faBox} />
                                {translations.productName || 'Product Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.product_name && <small>{errors.product_name}</small>}
                    </div>

                    {/* Batch Number */}
                    <div className={`ozui-field ${errors.batch_no ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="batch_no"
                                id="batch_no"
                                value={formData.batch_no}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading || isEditMode}
                                className={!isEditMode && formData.batch_no && !formData.batch_no_manual ? 'auto-filled' : ''}
                            />
                            <label htmlFor="batch_no">
                                <FontAwesomeIcon icon={faTag} />
                                {translations.batchNumber || 'Batch Number'} 
                                {!isEditMode && !formData.batch_no && <span className="auto-hint">(Auto from product name - increments)</span>}
                                {!isEditMode && formData.batch_no && !formData.batch_no_manual && <span className="auto-generated">(Auto-generated - increments)</span>}
                                {!isEditMode && formData.batch_no && formData.batch_no_manual && <span className="manual-edited">(Manually edited)</span>}
                                {isEditMode && <span>(Read only)</span>}
                            </label>
                        </div>
                        {errors.batch_no && <small>{errors.batch_no}</small>}
                    </div>

                    {/* Barcode */}
                    <div className={`ozui-field ${errors.barcode ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="barcode"
                                id="barcode"
                                value={formData.barcode}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading || isEditMode}
                                className={!isEditMode && formData.barcode && !formData.barcode_manual ? 'auto-filled' : ''}
                                autoComplete="off"
                            />
                            <label htmlFor="barcode">
                                <FontAwesomeIcon icon={faBarcode} />
                                {translations.barcode || 'Barcode'} 
                                {!isEditMode && !formData.barcode && <span className="auto-hint">(Auto from product name - or scan)</span>}
                                {!isEditMode && formData.barcode && !formData.barcode_manual && <span className="auto-generated">(Auto-generated - editable)</span>}
                                {!isEditMode && formData.barcode && formData.barcode_manual && <span className="manual-edited">(Manually edited)</span>}
                                {isEditMode && <span>(Read only)</span>}
                                {isScannerActive && !isEditMode && formData.barcode && (
                                    <span className="scanner-indicator" style={{ color: '#10b981', fontSize: '0.65rem', marginLeft: '4px' }}>
                                        <FontAwesomeIcon icon={faBarcode} /> ✓
                                    </span>
                                )}
                            </label>
                        </div>
                        {errors.barcode && <small>{errors.barcode}</small>}
                    </div>

                    {/* Category */}
                    <div className={`ozui-field ${errors.category ? 'ozui-error' : ''}`} ref={categoryDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="category"
                                value={categorySearchTerm}
                                onChange={(e) => {
                                    setCategorySearchTerm(e.target.value);
                                    setShowCategoryDropdown(true);
                                    if (formData.category && !categoryOptions.find(c => 
                                        c.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, category: '' }));
                                    }
                                }}
                                onFocus={() => setShowCategoryDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="category">
                                <FontAwesomeIcon icon={faLayerGroup} />
                                {translations.category || 'Category'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.category && <small>{errors.category}</small>}
                        {showCategoryDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(cat => (
                                        <div
                                            key={cat.value}
                                            className={`ozui-category-option ${formData.category === cat.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectCategory(cat)}
                                        >
                                            {cat.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No categories found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Company */}
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="company"
                                id="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="company">
                                <FontAwesomeIcon icon={faBuilding} />
                                {translations.company || 'Company / Brand'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    {/* Warranty */}
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="warranty"
                                id="warranty"
                                value={formData.warranty || ''}
                                onChange={handleChange}
                                placeholder=" "
                                maxLength={255}
                                disabled={loading}
                            />
                            <label htmlFor="warranty">
                                <FontAwesomeIcon icon={faShieldAlt} />
                                {translations.warranty || 'Warranty'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    {/* Color */}
                    <div className={`ozui-field ${errors.color ? 'ozui-error' : ''}`} ref={colorDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="color"
                                value={colorSearchTerm}
                                onChange={(e) => {
                                    setColorSearchTerm(e.target.value);
                                    setShowColorDropdown(true);
                                    if (formData.color && !colorOptions.find(c => 
                                        c.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, color: '' }));
                                    }
                                }}
                                onFocus={() => setShowColorDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="color">
                                <FontAwesomeIcon icon={faPalette} />
                                {translations.color || 'Color'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.color && <small>{errors.color}</small>}
                        {showColorDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredColors.length > 0 ? (
                                    filteredColors.map(color => (
                                        <div
                                            key={color.value}
                                            className={`ozui-category-option ${formData.color === color.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectColor(color)}
                                        >
                                            {color.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No colors found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Size */}
                    <div className={`ozui-field ${errors.size ? 'ozui-error' : ''}`} ref={sizeDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="size"
                                value={sizeSearchTerm}
                                onChange={(e) => {
                                    setSizeSearchTerm(e.target.value);
                                    setShowSizeDropdown(true);
                                    if (formData.size && !sizeOptions.find(s => 
                                        s.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, size: '' }));
                                    }
                                }}
                                onFocus={() => setShowSizeDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="size">
                                <FontAwesomeIcon icon={faRuler} />
                                {translations.size || 'Size'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.size && <small>{errors.size}</small>}
                        {showSizeDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredSizes.length > 0 ? (
                                    filteredSizes.map(size => (
                                        <div
                                            key={size.value}
                                            className={`ozui-category-option ${formData.size === size.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectSize(size)}
                                        >
                                            {size.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No sizes found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* GAUGE */}
                    <div className={`ozui-field ${errors.gauge ? 'ozui-error' : ''}`} ref={gaugeDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="gauge"
                                value={gaugeSearchTerm}
                                onChange={(e) => {
                                    setGaugeSearchTerm(e.target.value);
                                    setShowGaugeDropdown(true);
                                    if (formData.gauge && !gaugeOptions.find(g => 
                                        g.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, gauge: '' }));
                                    }
                                }}
                                onFocus={() => setShowGaugeDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="gauge">
                                <FontAwesomeIcon icon={faRuler} />
                                {translations.gauge || 'Gauge'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.gauge && <small>{errors.gauge}</small>}
                        {showGaugeDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredGauges.length > 0 ? (
                                    filteredGauges.map(gauge => (
                                        <div
                                            key={gauge.value}
                                            className={`ozui-category-option ${formData.gauge === gauge.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectGauge(gauge)}
                                        >
                                            {gauge.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No gauges found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* WEIGHT */}
                    <div className={`ozui-field ${errors.weight ? 'ozui-error' : ''}`} ref={weightDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="weight"
                                value={weightSearchTerm}
                                onChange={(e) => {
                                    setWeightSearchTerm(e.target.value);
                                    setShowWeightDropdown(true);
                                    if (formData.weight && !weightOptions.find(w => 
                                        w.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, weight: '' }));
                                    }
                                }}
                                onFocus={() => setShowWeightDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="weight">
                                <FontAwesomeIcon icon={faBalanceScale} />
                                {translations.weight || 'Weight'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.weight && <small>{errors.weight}</small>}
                        {showWeightDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredWeights.length > 0 ? (
                                    filteredWeights.map(weight => (
                                        <div
                                            key={weight.value}
                                            className={`ozui-category-option ${formData.weight === weight.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectWeight(weight)}
                                        >
                                            {weight.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No weights found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* CLASS */}
                    <div className={`ozui-field ${errors.class ? 'ozui-error' : ''}`} ref={classDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="class"
                                value={classSearchTerm}
                                onChange={(e) => {
                                    setClassSearchTerm(e.target.value);
                                    setShowClassDropdown(true);
                                    if (formData.class && !classOptions.find(c => 
                                        c.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, class: '' }));
                                    }
                                }}
                                onFocus={() => setShowClassDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="class">
                                <FontAwesomeIcon icon={faLayerGroup} />
                                {translations.class || 'Class'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.class && <small>{errors.class}</small>}
                        {showClassDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredClasses.length > 0 ? (
                                    filteredClasses.map(classOpt => (
                                        <div
                                            key={classOpt.value}
                                            className={`ozui-category-option ${formData.class === classOpt.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectClass(classOpt)}
                                        >
                                            {classOpt.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No classes found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ============================================== */}
                    {/* UNIT OF MEASURE */}
                    {/* ============================================== */}
                    <div className={`ozui-field ${errors.gunit ? 'ozui-error' : ''}`} ref={unitDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="gunit"
                                value={unitSearchTerm}
                                onChange={(e) => {
                                    setUnitSearchTerm(e.target.value);
                                    setShowUnitDropdown(true);
                                    if (formData.gunit && !unitOptions.find(u => 
                                        u.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, gunit: '' }));
                                    }
                                }}
                                onFocus={() => setShowUnitDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="gunit">
                                <FontAwesomeIcon icon={faRuler} />
                                {translations.unit || 'Unit'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.gunit && <small>{errors.gunit}</small>}
                        {showUnitDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredUnits.length > 0 ? (
                                    filteredUnits.map(unit => (
                                        <div
                                            key={unit.value}
                                            className={`ozui-category-option ${formData.gunit === unit.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectUnit(unit)}
                                        >
                                            {unit.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-unit-empty">No units found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* SUPPLIER */}
                    <div className={`ozui-field ${errors.supplier_id ? 'ozui-error' : ''}`} ref={supplierDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="supplier"
                                value={supplierSearchTerm}
                                onChange={handleSupplierSearchChange}
                                onFocus={() => {
                                    setShowSupplierDropdown(true);
                                    if (suppliers.length === 0) fetchSuppliers();
                                }}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="supplier">
                                <FontAwesomeIcon icon={faUserTie} />
                                {translations.supplier || 'Supplier'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.supplier_id && <small>{errors.supplier_id}</small>}
                        {showSupplierDropdown && (
                            <div className="ozui-category-dropdown">
                                {loadingSuppliers ? (
                                    <div className="ozui-supplier-loading">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>Loading suppliers...</span>
                                    </div>
                                ) : filteredSuppliers.length > 0 ? (
                                    filteredSuppliers.map(supplier => (
                                        <div
                                            key={supplier.supplierid}
                                            className={`ozui-category-option ${formData.supplier_id === supplier.supplierid ? 'ozui-selected' : ''}`}
                                            onClick={() => selectSupplier(supplier)}
                                        >
                                            <div className="ozui-supplier-option-name">
                                                <strong>{supplier.supplier_name}</strong>
                                                {supplier.company && (
                                                    <span className="ozui-supplier-company">({supplier.company})</span>
                                                )}
                                            </div>
                                            <div className="ozui-supplier-option-details">
                                                {supplier.phone && <span className="ozui-supplier-phone">{supplier.phone}</span>}
                                                {supplier.contact_person && <span className="ozui-supplier-contact">{supplier.contact_person}</span>}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-supplier-empty">
                                        {supplierSearchTerm ? 'No suppliers found' : 'Type to search suppliers'}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Cost Price Per Pack */}
                    <div className={`ozui-field ${errors.cost_price_per_pack ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="cost_price_per_pack"
                                id="cost_price_per_pack"
                                value={formData.cost_price_per_pack}
                                onChange={handleChange}
                                required
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="cost_price_per_pack">
                                <FontAwesomeIcon icon={faRupeeSign} />
                                {translations.costPerPack || 'Cost Per Packet'} <span>*</span>
                            </label>
                        </div>
                        {errors.cost_price_per_pack && <small>{errors.cost_price_per_pack}</small>}
                    </div>

                    {/* Selling Price Per Packet */}
                    <div className={`ozui-field ${errors.sell_price_per_unit ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="sell_price_per_unit"
                                id="sell_price_per_unit"
                                value={formData.sell_price_per_unit}
                                onChange={handleChange}
                                required
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="sell_price_per_unit">
                                <FontAwesomeIcon icon={faRupeeSign} />
                                {translations.sellingPerPack || 'Sell Price Per Packet'} <span>*</span>
                            </label>
                        </div>
                        {errors.sell_price_per_unit && <small>{errors.sell_price_per_unit}</small>}
                    </div>

                    {/* Total Quantity */}
                    <div className={`ozui-field ${errors.total_quantity ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="total_quantity"
                                id="total_quantity"
                                value={formData.total_quantity}
                                onChange={handleChange}
                                required
                                step="0.001"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="total_quantity">
                                <FontAwesomeIcon icon={faWarehouse} />
                                {translations.totalQuantity || 'Total Quantity'} <span>*</span>
                            </label>
                        </div>
                        {errors.total_quantity && <small>{errors.total_quantity}</small>}
                    </div>
                </div>
                
                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button 
                        type="submit" 
                        className="ozui-btn ozui-btn-primary" 
                        disabled={loading || !currentShop || isSubmittingRef.current}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{isEditMode ? (translations.updating || 'Updating...') : (translations.adding || 'Adding...')}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations.updateProduct || 'Update Product') : (translations.addProductButton || 'Save Product')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SolarSpecialProducts;
